import { message } from 'antd'
import useLocalRequest, { ResponseBody } from './useLocalRequest'
import { UserInfo } from './useUser'

const requestErrorMessage = (
  res: ResponseBody<unknown>,
  fallback: string,
): string => {
  if (res.errorMessages) {
    return Array.isArray(res.errorMessages)
      ? res.errorMessages[0]
      : String(res.errorMessages)
  }
  return fallback
}

export type DeliveryItem = {
  businessLineId: number
  businessLineName?: string
  createTime: string
  creator: string
  finishDate: string
  projectId:string
  projectName:string
  id: string
  lastOperator: string
  morningMeetingId: number
  morningMeetingName?: string
  orderNum: number
  remarks: string
  taskName: string
  taskSize?: 0 | 1 | 2 | 3
  taskStatusId: number
  taskStatusName?: string
  taskTypeId: number
  taskTypeName?: string
  taskUrl: string
  updateTime: string
  userList: UserInfoItem[]
}

export type ProjectIndex = {
  projectId:number,
  projectName:string
}


export type UserInfoItem = {
  createTime: string
  creator: string
  deliveryTaskId: string
  user: UserInfo
  id: string
  lastOperator: string
  userId: string
  updateTime: string
  userType: number
}

export type BizLintOption = {
  type: number
  name: string
}

export type TaskTypeOption = {
  type: number
  name: string
}

export type TaskStatusOption = {
  type: number
  name: string
}

export type SubmitDeliveryItem = { 
  businessLineName?: string
  finishDate: string
  morningMeetingId: number
  morningMeetingName?: string
  userId: string
  orderNum: number
  remarks: string
  taskName: string
  taskSize: 0 | 1 | 2 | 3
  taskStatusId: number
  taskStatusName?: string
  taskTypeId: number
  taskTypeName?: string
  taskUrl: string
  userList: { userId: string; userType: number }[]
}

export type DeliveryConfigOption = {
  id: number
  name: string
  type: number
  orderNum: number
  deleteFlag: 0 | 1
}

export type CommentInfo = {
  commentId: string
  content: string
  createTime: string
  creator: string
  extraInfo: string
  fromUser: UserInfo
  fromUserId: string
  replyCommentId: string
  targetId: string
  targetType: string
  toUser: UserInfo
  toUserId: string
  updateTime: string
}

export type OperationInfo = {
  content: string
  userName: string
  userId:number
  id: string
  taskId: string
  eventTime: Date
}

type Props = {
  // getUserListByName: (payload: { name: string }) => Promise<UserInfo[]>
  getAllUserList: () => Promise<UserInfo[]>
  getDeliveryList: (payload: { finishDate: string }) => Promise<DeliveryItem[]> 
  createDeliveryItem: (payload: SubmitDeliveryItem) => Promise<boolean>
  updateDeliveryItems: (payload: {
    deliveryWeekTaskList: (SubmitDeliveryItem & { id: string })[]
  }) => Promise<boolean>
  deleteDeliveryItem: (payload: { id: string }) => Promise<boolean>
  getDeliveryConfigType: (payload: {
    deliveryConfigType: number
  }) => Promise<DeliveryConfigOption[]>

  createDeliveryConfigType: (payload: {
    type: number
    name: string
    orderNum: number
  }) => Promise<boolean>
  updateDeliveryConfigType: (payload: {
    id: string
    type: number
    name: string
    orderNum: number
  }) => Promise<boolean>
  deleteDeliveryConfigType: (payload: {
    type: number
    id: string
  }) => Promise<boolean>
  getCommentListByTargetIds: (payload: string[]) => Promise<CommentInfo[]>
  createComment: (payload: {
    commentId?: string
    content: string
    extraInfo?: string
    fromUserId?: string
    replyCommentId?: string
    targetId: string
    targetType: 'MOMENT' | 'SPARROW'
    toUserId: string
  }) => Promise<boolean>
  deleteComment: (payload: string) => Promise<boolean>
  getOperationLogById: (payload: { taskId: string }) => Promise<OperationInfo[]>
}

function useDelivery(): Props {
  // 通过名字获取成员列表数据
  // const { run: getUserListByNameRun } = useLocalRequest<UserInfo[]>(
  //   '/sparrow/getFeiShuUserDTOListByName'
  // )
  // 获取全量成员数据
  const { run: getAllUserListRun } = useLocalRequest<UserInfo[]>(
    '/sparrowUser/getUserListByTeamId'
  )
  // 查询交付项列表
  const { run: getDeliveryRun } = useLocalRequest<DeliveryItem[]>(
    '/sparrow/queryDelivery'
  )
  // 创建交付项
  const { run: createDeliveryItemRun } = useLocalRequest<boolean>(
    '/sparrow/addDelivery'
  )
  // 批量更新交付项信息
  const { run: updateDeliveryItemsRun } = useLocalRequest<boolean>(
    '/sparrow/updateDelivery'
  )
  // 删除交付项信息
  const { run: deleteDeliveryItemRun } = useLocalRequest<boolean>(
    '/sparrow/deleteDelivery'
  )
  // 获取交付项配置信息
  const { run: getDeliveryConfigTypeRun } = useLocalRequest<
    DeliveryConfigOption[]
  >('/sparrow/queryDeliveryConfigType')
  // 添加交付项配置信息
  const { run: createDeliveryConfigTypeRun } = useLocalRequest<boolean>(
    '/sparrow/addDeliveryConfigType'
  )
  // 添加交付项配置信息
  const { run: updateDeliveryConfigTypeRun } = useLocalRequest<boolean>(
    '/sparrow/updateDeliveryConfigType'
  )
  // 添加交付项配置信息
  const { run: deleteDeliveryConfigTypeRun } = useLocalRequest<boolean>(
    '/sparrow/deleteDeliveryConfigType'
  )
  // 通过目标id群获取评论列表
  const { run: getCommentListByTargetIdsRun } = useLocalRequest<CommentInfo[]>(
    '/comment/findCommentListByTargetIdList'
  )
  // 创建评论
  const { run: createCommentRun } = useLocalRequest<boolean>(
    '/comment/addComment'
  )
  // 删除评论
  const { run: deleteCommentRun } = useLocalRequest<boolean>('/comment/delete')
  // 获取操作日志
  const { run: getOperationLogByIdRun } = useLocalRequest<OperationInfo[]>(
    '/sparrow/getTaskOperateLog'
  )

  return {
    // getUserListByName: (payload) =>
    //   getUserListByNameRun(payload).then(
    //     ({ success, result }) => success && result
    //   ),
    getAllUserList: () =>
      getAllUserListRun().then(({ success, result }) => success && result),
    getDeliveryList: (payload) =>
      getDeliveryRun(payload).then(({ success, result }) => success && result),
    createDeliveryItem: (payload) =>
      createDeliveryItemRun(payload).then(
        ({ success, result }) => success && result
      ),
    updateDeliveryItems: (payload) =>
      updateDeliveryItemsRun(payload).then(
        ({ success, result }) => success && result
      ),
    deleteDeliveryItem: (payload) =>
      deleteDeliveryItemRun(payload).then(
        ({ success, result }) => success && result
      ),
    getDeliveryConfigType: (payload) =>
      getDeliveryConfigTypeRun(payload).then(
        ({ success, result }) => success && result
      ),
    createDeliveryConfigType: (payload) =>
      createDeliveryConfigTypeRun(payload).then(
        ({ success, result }) => success && result
      ),
    updateDeliveryConfigType: (payload) =>
      updateDeliveryConfigTypeRun(payload).then(
        ({ success, result }) => success && result
      ),
    deleteDeliveryConfigType: (payload) =>
      deleteDeliveryConfigTypeRun(payload).then(
        ({ success, result }) => success && result
      ),
    getCommentListByTargetIds: (payload) =>
      getCommentListByTargetIdsRun(payload).then(
        ({ success, result }) => success && result
      ),
    createComment: (payload) =>
      createCommentRun(payload).then((res) => {
        if (res.success && res.result) {
          return true
        }
        message.error(requestErrorMessage(res, '评论发表失败，请稍后重试'))
        return false
      }),
    deleteComment: (payload) =>
      deleteCommentRun(payload).then(
        ({ success, result }) => success && result
      ),
    getOperationLogById: (payload) =>
      getOperationLogByIdRun(payload).then(
        ({ success, result }) => success && result
      )
  }
}

export default useDelivery
